/**
 * Site/session packet completion dashboard (admin/editor).
 * Usage: /admin/packet-dashboard?session=<id>
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  Link,
} from '@mui/material';
import api, { getCurrentSiteId } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function fmtIso(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function unsignedFormsSummary(row) {
  const m = row.forms_signed_map;
  if (!m || typeof m !== 'object') return '';
  const ids = Object.entries(m)
    .filter(([, v]) => !v)
    .map(([k]) => k);
  if (!ids.length) return '';
  const shown = ids.slice(0, 5).join(', ');
  return ids.length > 5 ? `${shown}…` : shown;
}

export default function PacketDashboardPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionParam = searchParams.get('session') || '';
  const siteParam = searchParams.get('site') || '';
  const [sessionId, setSessionId] = useState(sessionParam);
  const [siteFilter, setSiteFilter] = useState(() => {
    if (siteParam) return siteParam;
    const chromeSiteId = getCurrentSiteId();
    return chromeSiteId ? String(chromeSiteId) : '';
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [dashMeta, setDashMeta] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selected, setSelected] = useState({});
  const canBulkReset = user?.role === 'admin' || user?.role === 'superadmin';

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError('');
    try {
      const dashParams = { session: sessionId, detail: '1' };
      if (siteFilter.trim()) dashParams.site = siteFilter.trim();
      const [dash, ana] = await Promise.all([
        api.get(`/sessions/packet-dashboard/`, { params: dashParams }),
        api.get(`/sessions/packet-analytics/`, { params: { session: sessionId, ...(siteFilter.trim() ? { site: siteFilter.trim() } : {}) } }),
      ]);
      setRows(dash.data.results || []);
      setDashMeta({ site_id: dash.data.site_id, site_slug: dash.data.site_slug });
      setAnalytics(ana.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
      setRows([]);
      setDashMeta(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId, siteFilter]);

  useEffect(() => {
    if (sessionParam) {
      setSessionId(sessionParam);
    }
    if (siteParam) {
      setSiteFilter(siteParam);
    } else {
      const chromeSiteId = getCurrentSiteId();
      if (chromeSiteId) setSiteFilter(String(chromeSiteId));
    }
  }, [sessionParam, siteParam]);

  useEffect(() => {
    if (sessionId) load();
  }, [sessionId, load]);

  const toggle = (id) => {
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => Number(k));

  const bulk = async (action) => {
    if (!selectedIds.length) return;
    if (action === 'reset' && !canBulkReset) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/sessions/packet-bulk/', { action, student_ids: selectedIds });
      setSelected({});
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Bulk action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Parent packet dashboard
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Session ID"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            label="Site (id or slug, optional)"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            placeholder="e.g. 14th-ave"
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            onClick={() => {
              const p = { session: sessionId };
              if (siteFilter.trim()) p.site = siteFilter.trim();
              setSearchParams(p);
              load();
            }}
          >
            Load
          </Button>
          <Button variant="outlined" disabled={!selectedIds.length || loading} onClick={() => bulk('send')}>
            Bulk send
          </Button>
          <Button variant="outlined" disabled={!selectedIds.length || loading} onClick={() => bulk('remind')}>
            Bulk remind
          </Button>
          {canBulkReset && (
            <Button
              variant="outlined"
              color="error"
              disabled={!selectedIds.length || loading}
              onClick={() => {
                if (window.confirm('Reset parent packets for selected students? This cannot be undone.')) bulk('reset');
              }}
            >
              Bulk reset (admin)
            </Button>
          )}
        </Box>
      </Paper>
      {dashMeta && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Session site: {dashMeta.site_slug || dashMeta.site_id || '—'}
        </Typography>
      )}
      {analytics?.by_event_type?.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Events:{' '}
          {analytics.by_event_type.map((x) => `${x.event_type}: ${x.n}`).join(' · ')}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && sessionId && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Student</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>New</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Signed</TableCell>
              <TableCell align="right">%</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell align="right">Days</TableCell>
              <TableCell>Last remind</TableCell>
              <TableCell>Unsigned forms</TableCell>
              <TableCell>Archived</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.student_id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={!!selected[r.student_id]}
                    onChange={() => toggle(r.student_id)}
                  />
                </TableCell>
                <TableCell>
                  <Link component={RouterLink} to={r.student_url_path || `/students/${r.student_id}`} underline="hover">
                    {r.last_name}, {r.first_name}
                  </Link>
                </TableCell>
                <TableCell>{r.site_slug || '—'}</TableCell>
                <TableCell>{r.is_new_student ? 'Y' : ''}</TableCell>
                <TableCell>{r.overall}</TableCell>
                <TableCell align="right">
                  {r.forms_signed}/{r.forms_total}
                </TableCell>
                <TableCell align="right">{r.pct_complete != null ? `${r.pct_complete}%` : '—'}</TableCell>
                <TableCell>{fmtIso(r.sent_at)}</TableCell>
                <TableCell align="right">{r.days_since_sent != null ? r.days_since_sent : '—'}</TableCell>
                <TableCell>{fmtIso(r.last_reminder_at)}</TableCell>
                <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={unsignedFormsSummary(r)}>
                  {unsignedFormsSummary(r) || '—'}
                </TableCell>
                <TableCell>{r.archived_pdf_url ? 'Yes' : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
